import { BottomSheet } from '@liveauctioneers/hammer-ui-core/bottomSheet';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { Camera, ImageSquare } from '@liveauctioneers/hammer-ui-core/icons';
import { Loading } from '@liveauctioneers/hammer-ui-core/loading';
import { BodySecondary, Caption } from '@liveauctioneers/hammer-ui-core/text';
import { type ChangeEvent, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useImageSearch } from '@/hooks/useImageSearch';
import { submitImageSearch } from '@/redux/modules/search/imageSearch/imageSearch.actions';
import { ImageSearchUploadMethod } from '@/utils/amplitude/analytics/core.types';
import { useTrackImageUploadedAnalytics } from '@/utils/amplitude/analytics/searchByImage/trackImageUploaded.analytics';
import { useAppFormatMessage } from '@/utils/intl/useAppFormatMessage';

// "Take a photo" opens the rear camera directly; "Choose a photo" omits `capture` so it
// opens the photo library. Both run the same client flow as the desktop dropzone — the
// thunk's type pre-check is the single source of the error state, so the library input
// matches the thunk's accepted types while the camera stays open to any captured image.
const LIBRARY_ACCEPT = 'image/jpeg,image/png,image/webp,image/heic,image/heif';

type Props = {
    onClose: () => void;
    open: boolean;
};

/**
 * Mobile upload surface for Search by Image: a bottom sheet with native camera / photo-library
 * entry points, plus the analysing + error states per the SEAR-1251 Figma. The desktop surface
 * is SearchByImagePopover; both consume the shared image-search client flow and, on success,
 * navigate to the results page that renders from the imageSearch slice.
 */
export const SearchByImageBottomSheet = ({ onClose, open }: Props) => {
    const formatMessage = useAppFormatMessage();
    const navigate = useNavigate();
    const trackImageUploaded = useTrackImageUploadedAnalytics();
    // Stale state is cleared by the entry point before it opens this sheet.
    const { error, status, submit } = useImageSearch();
    const cameraInputRef = useRef<HTMLInputElement>(null);
    const libraryInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = useCallback(
        async (event: ChangeEvent<HTMLInputElement>, uploadMethod: ImageSearchUploadMethod) => {
            const file = event.target.files?.[0];
            // Reset so re-selecting the same file still fires a change event.
            event.target.value = '';
            if (!file) {
                return;
            }
            trackImageUploaded({ uploadMethod });
            const action = await submit(file);
            if (submitImageSearch.fulfilled.match(action)) {
                onClose();
                navigate('/visual-search');
            }
        },
        [navigate, onClose, submit, trackImageUploaded]
    );

    const isAnalysing = status === 'analysing';
    const errorMessage =
        status === 'error'
            ? formatMessage(['visualSearch', error === 'invalid-type' ? 'typeError' : 'serverError'])
            : null;

    return (
        <BottomSheet
            // Static, content-sized sheet — disable the sheet's own scroll so it doesn't
            // render a scrollbar when the content nearly fills the snap height.
            data-testid="searchByImageBottomSheet"
            disableScroll
            footer={
                <Button
                    className="w-full"
                    onClick={onClose}
                    style="neutral"
                    type="button"
                    variant="transparent"
                >
                    {formatMessage(['visualSearch', 'close'])}
                </Button>
            }
            header={{
                description: formatMessage(['visualSearch', 'findSimilarSubtitle']),
                title: formatMessage(['visualSearch', 'searchByImage']),
            }}
            onDismiss={onClose}
            open={open}
        >
            <div className="flex flex-col gap-xs px-md pb-md pt-2xs">
                {isAnalysing ? (
                    <div
                        className="flex flex-col items-center gap-sm py-md"
                        data-testid="searchByImageAnalysing"
                    >
                        <Loading />
                        <BodySecondary>{formatMessage(['visualSearch', 'analysing'])}</BodySecondary>
                    </div>
                ) : (
                    <>
                        {errorMessage ? <Caption className="text-text-critical">{errorMessage}</Caption> : null}
                        <Button
                            className="w-full"
                            iconLeft={Camera}
                            onClick={() => {
                                cameraInputRef.current?.click();
                            }}
                            style="neutral"
                            type="button"
                            variant="outlined"
                        >
                            {formatMessage(['visualSearch', 'takePhoto'])}
                        </Button>
                        <Button
                            className="w-full"
                            iconLeft={ImageSquare}
                            onClick={() => {
                                libraryInputRef.current?.click();
                            }}
                            style="neutral"
                            type="button"
                            variant="outlined"
                        >
                            {formatMessage(['visualSearch', 'choosePhoto'])}
                        </Button>
                    </>
                )}
                {/* Raw inputs: a camera-capture file picker has no hammer-ui equivalent, and the
                    desktop dropzone likewise renders a native input under the hood. */}
                <input
                    accept="image/*"
                    aria-label={String(formatMessage(['visualSearch', 'takePhoto']))}
                    capture="environment"
                    className="hidden"
                    onChange={(event) => {
                        handleFileChange(event, ImageSearchUploadMethod.Camera);
                    }}
                    ref={cameraInputRef}
                    type="file"
                />
                <input
                    accept={LIBRARY_ACCEPT}
                    aria-label={String(formatMessage(['visualSearch', 'choosePhoto']))}
                    className="hidden"
                    onChange={(event) => {
                        handleFileChange(event, ImageSearchUploadMethod.PhotoLibrary);
                    }}
                    ref={libraryInputRef}
                    type="file"
                />
            </div>
        </BottomSheet>
    );
};
